export interface ValidationError {
  field: string;
  message: string;
}

export interface PromoCodeFormData {
  discount_type: 'fixed' | 'percentage';
  discount_value: number | '' | undefined;
  start_date: string;
  end_date: string;
  min_order_amount?: number;
  max_order_amount?: number;
  max_uses_per_user?: number | '';
}

/**
 * Validates promo code form data
 * @returns Array of validation errors, empty if valid
 */
export function validatePromoCodeForm(
  formData: PromoCodeFormData,
  isUpdate = false
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (formData.start_date) {
    const startDate = new Date(formData.start_date);
    if (!isUpdate && startDate < today) {
      errors.push({
        field: 'start_date',
        message: 'Start date cannot be in the past'
      });
    }
  }

  if (formData.end_date) {
    const endDate = new Date(formData.end_date);
    if (!isUpdate && endDate < today) {
      errors.push({
        field: 'end_date',
        message: 'End date cannot be in the past'
      });
    }
  }

  if (formData.start_date && formData.end_date) {
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (startDate > endDate) {
      errors.push({
        field: 'end_date',
        message: 'The start date must be earlier than or equal to the end date'
      });
    }
  }

  // Validate order amounts
  const minAmount = formData.min_order_amount || 0;
  const maxAmount = formData.max_order_amount || 0;
  if (maxAmount > 0 && minAmount > 0 && maxAmount < minAmount) {
    errors.push({
      field: 'max_order_amount',
      message: 'Max subtotal amount cannot be less than min subtotal amount'
    });
  }

  // Validate discount value - required field
  if (formData.discount_value === undefined || formData.discount_value === null || formData.discount_value === 0 || formData.discount_value === '') {
    errors.push({
      field: 'discount_value',
      message: 'The discount value is required and cannot be 0'
    });
  } else {
    // Percentage validation
    if (formData.discount_type === 'percentage') {
      if (formData.discount_value < 0 || formData.discount_value > 100) {
        errors.push({
          field: 'discount_value',
          message: 'The discount percentage must be between 0 and 100'
        });
      }
    }
  }

  // Multi-use code requires a "Uses per user" value. The form represents
  // "Multi-use enabled, infinite unchecked, no value entered" as an empty
  // string, which would otherwise be silently saved as single-use (0).
  if (formData.max_uses_per_user === '') {
    errors.push({
      field: 'max_uses_per_user',
      message: 'Uses per user is required when Multi-use code is enabled'
    });
  }

  return errors;
}

/**
 * Get a user-friendly error message from validation errors
 */
export function getValidationErrorMessage(errors: ValidationError[]): string {
  if (errors.length === 0) return '';
  if (errors.length === 1) return errors[0].message;
  return errors.map(e => e.message).join('; ');
}
